import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep
} from '@/components/ai-elements/chain-of-thought'
import { Loader } from '@/components/ai-elements/loader'
import { Shimmer } from '@/components/ai-elements/shimmer'
import type { ChatMessage } from '@/lib/ai-team'
import { ToolLogo } from '@/lib/tool-icons'
import { cn } from '@/lib/utils'
import {
  AlertTriangle,
  BarChart3,
  Box,
  Calendar,
  CircleHelp,
  Globe,
  HeartPulse,
  ImageIcon,
  Inbox,
  type LucideIcon,
  Mail,
  Megaphone,
  Newspaper,
  NotebookPen,
  Plug,
  Scale,
  Search,
  ShieldCheck,
  Store,
  Truck,
  User,
  Users,
  Wallet,
  Workflow as WorkflowIcon,
  Wrench,
  X
} from 'lucide-react'
import { type ComponentProps, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { AgentAvatar, useFresh } from './AgentAvatar'

const ICONS: Record<string, LucideIcon> = {
  team: Users,
  image: ImageIcon,
  inbox: Inbox,
  question: CircleHelp,
  note: NotebookPen,
  mail: Mail,
  chart: BarChart3,
  health: HeartPulse,
  money: Wallet,
  person: User,
  box: Box,
  truck: Truck,
  calendar: Calendar,
  workflow: WorkflowIcon,
  store: Store,
  shield: ShieldCheck,
  search: Search,
  alert: AlertTriangle,
  globe: Globe,
  news: Newspaper,
  megaphone: Megaphone,
  scale: Scale,
  plug: Plug,
  tool: Wrench
}

export type ChatItem =
  | { type: 'message'; message: ChatMessage }
  | { type: 'work'; key: number; agent: string; steps: ChatMessage[] }

/** Messages that belong inside another step (a teammate's work under "Asked X"), by parent step id. */
export type Nested = Map<number, ChatMessage[]>

/**
 * Top-level items: consecutive steps by the same agent become one work block,
 * like Claude's thinking panel. A teammate's steps and reply are nested under
 * the lead's "asked X" step instead of appearing as separate messages.
 */
export function groupMessages(messages: ChatMessage[]): {
  items: ChatItem[]
  nested: Nested
} {
  const nested: Nested = new Map()
  const items: ChatItem[] = []
  for (const m of messages) {
    const parent = m.data.parent
    if (parent) {
      nested.set(parent, [...(nested.get(parent) ?? []), m])
      continue
    }
    const last = items[items.length - 1]
    if (m.role === 'step') {
      const agent = m.agent?.name ?? 'Agent'
      if (last?.type === 'work' && last.agent === agent) last.steps.push(m)
      else items.push({ type: 'work', key: m.id, agent, steps: [m] })
    } else {
      items.push({ type: 'message', message: m })
    }
  }
  return { items, nested }
}

/** The connected app a step used (Instagram, Slack…), if any. */
// Tools that work on one outside app show its logo even without an `account` arg.
const TOOL_ACCOUNTS: Record<string, string> = { drive_files: 'googledrive' }

const stepToolkit = (s: ChatMessage) => {
  const account = s.data.args?.account
  if (typeof account === 'string') return account
  return TOOL_ACCOUNTS[s.data.tool ?? ''] ?? ''
}

const lower = (s: string) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s)

function took(steps: ChatMessage[]) {
  const end = steps[steps.length - 1].data.finished_at
  if (!end) return ''
  const s = Math.round(
    (new Date(end).getTime() - new Date(steps[0].created_at).getTime()) / 1000
  )
  return s < 1
    ? ''
    : s < 60
      ? ` · ${s}s`
      : ` · ${Math.floor(s / 60)}m ${s % 60}s`
}

/** The innermost thing running right now, e.g. Billing checking payouts inside Chief's "asking Billing". */
function deepestRunning(
  steps: ChatMessage[],
  nested: Nested
): ChatMessage | undefined {
  const running = steps.find((s) => s.data.status === 'running')
  if (!running) return undefined
  const inner = (nested.get(running.id) ?? []).filter((m) => m.role === 'step')
  return deepestRunning(inner, nested) ?? running
}

function countSteps(steps: ChatMessage[], nested: Nested): number {
  return steps.reduce(
    (n, s) =>
      n +
      1 +
      countSteps(
        (nested.get(s.id) ?? []).filter((m) => m.role === 'step'),
        nested
      ),
    0
  )
}

export function WorkBlock({
  agent,
  steps,
  nested,
  live = false,
  answeredAt
}: {
  agent: string
  steps: ChatMessage[]
  nested: Nested
  /** When this agent's answer under these steps arrived; the orb smiles for a moment. */
  answeredAt?: string
  /** The agent is still on this turn (working or thinking); its answer isn't written yet. */
  live?: boolean
}) {
  const running = deepestRunning(steps, nested)
  const failed = steps.filter((s) => s.data.status === 'error').length
  const total = countSteps(steps, nested)
  const answered = useFresh(answeredAt)
  // Open while working, folded away once done, unless the admin chose otherwise.
  const [manual, setManual] = useState<boolean | null>(null)
  const open = manual ?? (Boolean(running) || live)

  const header =
    !running && live ? (
      <Shimmer>{`${agent} is thinking…`}</Shimmer>
    ) : running ? (
      <Shimmer>{`${running.agent?.name ?? agent} is ${lower(running.data.doing ?? running.text)}…`}</Shimmer>
    ) : (
      <span>
        <span className="font-medium">{agent}</span>{' '}
        {total === 1
          ? lower(steps[0].data.done ?? steps[0].text)
          : `worked through ${total} steps`}
        {failed > 0 && (
          <span className="text-red-600 dark:text-red-400">
            {' '}
            · {failed} failed
          </span>
        )}
        <span className="text-neutral-400">{took(steps)}</span>
      </span>
    )

  return (
    <ChainOfThought open={open} onOpenChange={setManual} className="space-y-2">
      <ChainOfThoughtHeader
        className="w-auto max-w-full"
        icon={
          <AgentAvatar
            name={running?.agent?.name ?? agent}
            size="xs"
            state={
              running
                ? 'working'
                : live
                  ? 'thinking'
                  : failed
                    ? 'error'
                    : answered
                      ? 'replying'
                      : 'idle'
            }
          />
        }
      >
        {header}
      </ChainOfThoughtHeader>
      <ChainOfThoughtContent>
        <div className="pt-2">
          <Steps steps={steps} nested={nested} />
        </div>
      </ChainOfThoughtContent>
    </ChainOfThought>
  )
}

function Steps({ steps, nested }: { steps: ChatMessage[]; nested: Nested }) {
  return (
    <>
      {steps.map((s, i) => {
        const status = s.data.status ?? 'done'
        const inner = nested.get(s.id) ?? []
        const innerSteps = inner.filter((m) => m.role === 'step')
        const replies = inner.filter((m) => m.role === 'agent')
        return (
          <ChainOfThoughtStep
            key={s.id}
            last={i === steps.length - 1}
            icon={
              status === 'error' ? X : (ICONS[s.data.icon ?? 'tool'] ?? Wrench)
            }
            iconNode={
              status !== 'error' && stepToolkit(s) ? (
                <ToolLogo
                  toolkit={stepToolkit(s)}
                  size="xs"
                  className="p-0 ring-0"
                />
              ) : undefined
            }
            status={
              status === 'running'
                ? 'active'
                : status === 'error'
                  ? 'error'
                  : 'complete'
            }
            label={
              status === 'running' ? (
                <Shimmer>{`${s.data.doing ?? s.text}…`}</Shimmer>
              ) : (
                (s.data.done ?? s.text)
              )
            }
            description={
              status === 'error'
                ? s.data.error || s.data.summary
                : inner.length
                  ? undefined
                  : s.data.summary
            }
          >
            {inner.length > 0 && (
              <Teammate steps={innerSteps} replies={replies} nested={nested} />
            )}
          </ChainOfThoughtStep>
        )
      })}
    </>
  )
}

/** A teammate's work, compact: one line per step, then a short reply you can expand. */
function Teammate({
  steps,
  replies,
  nested
}: { steps: ChatMessage[]; replies: ChatMessage[]; nested: Nested }) {
  const [full, setFull] = useState(false)
  return (
    <div className="mt-1 space-y-1 border-l border-neutral-200 pl-3 text-xs dark:border-neutral-800">
      {steps.map((s) => {
        const status = s.data.status ?? 'done'
        const Icon =
          status === 'error' ? X : (ICONS[s.data.icon ?? 'tool'] ?? Wrench)
        const inner = nested.get(s.id) ?? []
        return (
          <div key={s.id}>
            <p className="flex items-center gap-1.5 text-neutral-600 dark:text-neutral-300">
              {status === 'running' ? (
                <Loader size={12} className="text-neutral-400" />
              ) : stepToolkit(s) && status !== 'error' ? (
                <ToolLogo toolkit={stepToolkit(s)} size="xs" className="p-0" />
              ) : (
                <Icon
                  className={cn(
                    'size-3 shrink-0',
                    status === 'error' ? 'text-red-500' : 'text-neutral-400'
                  )}
                />
              )}
              {status === 'running' ? (
                <Shimmer>{`${s.data.doing ?? s.text}…`}</Shimmer>
              ) : (
                <span className="truncate">
                  {s.data.done ?? s.text}
                  {s.data.summary && (
                    <span className="text-neutral-400">
                      {' '}
                      · {s.data.summary}
                    </span>
                  )}
                </span>
              )}
            </p>
            {inner.length > 0 && (
              <Teammate
                steps={inner.filter((m) => m.role === 'step')}
                replies={inner.filter((m) => m.role === 'agent')}
                nested={nested}
              />
            )}
          </div>
        )
      })}
      {replies.map((r) => (
        <button
          key={r.id}
          type="button"
          onClick={() => setFull((v) => !v)}
          className="block w-full text-left text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
          title={full ? 'Show less' : 'Show the full reply'}
        >
          <span className="font-medium text-neutral-700 dark:text-neutral-200">
            {r.agent?.name}:{' '}
          </span>
          <span className={cn(!full && 'line-clamp-2')}>
            {r.text.replace(/\*\*/g, '')}
          </span>
        </button>
      ))}
    </div>
  )
}

/** "Chief of Staff is thinking…" between steps, before the first word appears. */
export function ThinkingRow({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-2.5 text-sm animate-in fade-in-0">
      <AgentAvatar name={name} size="sm" state="thinking" />
      <Shimmer>{`${name} is thinking…`}</Shimmer>
    </div>
  )
}

const md: ComponentProps<typeof ReactMarkdown>['components'] = {
  p: (p) => <p className="[&:not(:first-child)]:mt-3" {...p} />,
  ul: (p) => (
    <ul
      className="mt-3 list-disc space-y-1.5 pl-5 marker:text-neutral-400"
      {...p}
    />
  ),
  ol: (p) => (
    <ol
      className="mt-3 list-decimal space-y-1.5 pl-5 marker:text-neutral-400"
      {...p}
    />
  ),
  strong: (p) => (
    <strong className="font-semibold text-neutral-900 dark:text-white" {...p} />
  ),
  a: (p) => (
    <a
      className="underline underline-offset-2"
      target="_blank"
      rel="noreferrer"
      {...p}
    />
  ),
  h1: (p) => <p className="mt-2 font-bold" {...p} />,
  h2: (p) => <p className="mt-2 font-bold" {...p} />,
  h3: (p) => <p className="mt-2 font-semibold" {...p} />,
  hr: () => <hr className="my-3 border-neutral-200 dark:border-neutral-800" />,
  code: (p) => (
    <code
      className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[0.85em] dark:bg-neutral-800"
      {...p}
    />
  ),
  table: (p) => (
    <div className="mt-2 overflow-x-auto">
      <table className="w-full text-left text-xs" {...p} />
    </div>
  ),
  th: (p) => (
    <th
      className="border-b border-neutral-200 py-1 pr-3 font-semibold dark:border-neutral-800"
      {...p}
    />
  ),
  td: (p) => (
    <td
      className="border-b border-neutral-100 py-1 pr-3 dark:border-neutral-800/60"
      {...p}
    />
  )
}

export function AgentText({ text }: { text: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>
      {text}
    </ReactMarkdown>
  )
}
